import React, { useContext, useEffect, useState } from 'react';
import { Alert, Platform, StyleSheet, View } from 'react-native';
import { Button, Input, Text } from '@rneui/themed';
import { useForm, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import PasswordReset from './PasswordReset';
import Loading from '../Loading';
import { post } from '../../../common/Api';
import AppConstants from '../../../common/Constants';
import Utils from '../../../common/Utils';
import AppContext from '../../../contexts/App';
import AuthContext, { ActionTypes } from '../../../contexts/Auth';
import LayoutContext from '../../../contexts/Layout';

const Login = ({ setVisible }) => {
    const { state, dispatch } = useContext(AuthContext);
    const { state: appState } = useContext(AppContext);
    const { layout } = useContext(LayoutContext);
    const [showPasswordReset, setShowPasswordReset] = useState(false);

    const {
        clearErrors,
        control,
        formState: { isSubmitting, isValid },
        getValues,
        handleSubmit,
        reset
    } = useForm({
        mode: 'onBlur'
    });

    const handleLogin = async () => {
        const tokenEndpoint = state.authority?.token_endpoint;

        if (!tokenEndpoint) {
            throw new Error('Unable to login. Please contact support.');
        }

        const email = getValues('email');
        const password = getValues('password');

        const tokenRequestBody = {
            client_id: appState.settings.openIdSettings.thisClient.id,
            client_secret: appState.settings.openIdSettings.thisClient.secret,
            grant_type: 'password',
            password: password,
            scope: 'openid profile offline_access',
            username: email
        };

        const tokenResponse = await post(tokenEndpoint, new URLSearchParams(tokenRequestBody), undefined, 'application/x-www-form-urlencoded');

        if (tokenResponse?.access_token) {
            const userInfoEndpoint = state.authority?.userinfo_endpoint;

            if (userInfoEndpoint) {
                const userInfoResponse = await post(userInfoEndpoint, undefined, undefined, undefined, {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    Authorization: `Bearer ${tokenResponse.access_token}`
                });

                const userTidResponse = await post(AppConstants.REACT_APP_GET_USER_TID_BASE, undefined, undefined, undefined, {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    Authorization: tokenResponse.access_token
                });

                if (userInfoResponse && userTidResponse && userTidResponse.indexOf('html') === -1) {
                    dispatch({
                        type: ActionTypes.LOGIN,
                        data: {
                            accessToken: tokenResponse.access_token,
                            details: { ...userInfoResponse },
                            email,
                            loggedIn: true,
                            refreshToken: tokenResponse.refresh_token,
                            registered: true,
                            tid: userTidResponse,
                            tokenExpires: tokenResponse.expires_in,
                            tokenCreated: Date.now()
                        }
                    });

                    setVisible(false);
                    return;
                }
            }
        }

        if (layout.device.os.equals('web')) {
            window.alert('Login failed. Please try again.');
        } else {
            Alert.alert('Error', 'Login failed. Please try again.');
        }
    };

    useEffect(() => {
        return () => {
            clearErrors(['email', 'password']);
            reset();
        };
    }, [clearErrors, reset]);

    return (
        <View testID='Authentication.Login'>
            <Controller
                control={control}
                defaultValue=''
                name='email'
                render={({ field, fieldState }) => {
                    return (
                        <Input
                            {...field}
                            autoCorrect={false}
                            autoFocus={false}
                            errorMessage={fieldState.error?.message}
                            errorStyle={styles.errorText}
                            innerRef={field.ref}
                            inputStyle={{ outlineStyle: 'none' }}
                            placeholder='EMAIL'
                            placeholderTextColor='#8897a2'
                            textContentType='emailAddress'
                        />
                    );
                }}
                rules={{
                    required: {
                        message: 'Email is required',
                        value: true
                    },
                    pattern: {
                        message: 'Invalid email',
                        value: Utils.ValidEmailRegExp
                    }
                }}
            />
            <Controller
                control={control}
                defaultValue=''
                name='password'
                render={({ field, fieldState }) => {
                    return (
                        <Input
                            {...field}
                            autoCorrect={false}
                            errorMessage={fieldState.error?.message}
                            errorStyle={styles.errorText}
                            innerRef={field.ref}
                            inputStyle={{ outlineStyle: 'none' }}
                            maxLength={24}
                            placeholder='PASSWORD'
                            placeholderTextColor='#8897a2'
                            secureTextEntry={true}
                            textContentType='password'
                        />
                    );
                }}
                rules={{
                    required: {
                        message: 'Password is required',
                        value: true
                    }
                }}
            />
            <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
                <Text onPress={() => setShowPasswordReset(true)} style={styles.alternateActionText}>
                    Forgot Password
                </Text>
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'space-evenly' }}>
                <Button
                    buttonStyle={{ backgroundColor: '#153d6e' }}
                    containerStyle={{ marginVertical: 20, width: 200 }}
                    disabled={!isValid}
                    disabledStyle={{
                        backgroundColor: '#f7f8f9',
                        borderColor: '#000',
                        borderWidth: 0.25
                    }}
                    size='md'
                    title='SIGN IN'
                    titleStyle={{ ...layout.styleGuide.h5, color: '#fff' }}
                    onPress={handleSubmit(handleLogin)}
                />
                <Button
                    buttonStyle={{ backgroundColor: '#a61a2b' }}
                    containerStyle={{ marginVertical: 20, width: 200 }}
                    size='md'
                    title='CANCEL'
                    titleStyle={{ ...layout.styleGuide.h5, color: '#fff' }}
                    onPress={() => setVisible(false)}
                />
            </View>

            <PasswordReset setVisible={setShowPasswordReset} visible={showPasswordReset} />
            {isSubmitting && <Loading />}
        </View>
    );
};

const styles = StyleSheet.create({
    alternateActionText: {
        color: '#a61a2b',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '12pt' : 12
    },
    errorText: {
        color: '#a61a2b',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '14pt' : 14
    }
});

Login.propTypes = {
    setVisible: PropTypes.func.isRequired
};

export default Login;
